import api from '~api/api'
import {
  type AssetType,
  AssetTypeEnum,
  type IApiResponse,
  type IdentityId,
  type SourceAccessType,
} from '~types'

import {
  MultipartUploadError,
  abortMultipartUpload,
  createUploadSlots,
  uploadFilePresigned,
} from './presigned_upload_utils'

export interface IUploadDocumentsResponse {
  success: boolean
  uploadedDocuments: string[]
  errors?: string[]
}

export interface IUploadAssetResponse {
  key: string
  url: string
  is_secure: boolean
  accessible_url: string
}

export const uploadFile = async (
  userId: string,
  ownerId: string,
  file: File,
  assetType: AssetType,
  caseHandle: string | null,
  caseId: string | null,
  isSecure: boolean = false,
): Promise<IUploadAssetResponse> => {
  try {
    const formData = new FormData()

    formData.append('user_id', userId)
    formData.append('owner_id', ownerId)
    formData.append('file', file)
    formData.append('is_secure', isSecure.toString())
    formData.append('asset_type', assetType)
    if (caseHandle) {
      formData.append('case_handle', caseHandle)
    }
    if (caseId) {
      formData.append('case_id', caseId)
    }

    const response = await api.post<IApiResponse<IUploadAssetResponse>>(
      '/core/upload-asset/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    if (!response.data) {
      throw new Error('No data returned from upload API')
    }
    return response.data as IUploadAssetResponse
  } catch (error: unknown) {
    console.error('Error uploading asset:', error)
    throw error
  }
}

const MAX_STORAGE_NAME_LENGTH = 512

const uniqueStorageName = (fileName: string): string => {
  const suffix = `_${crypto.randomUUID().slice(0, 4)}`
  const extensionIndex = fileName.lastIndexOf('.')
  const baseName = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName
  const extension = extensionIndex > 0 ? fileName.slice(extensionIndex) : ''
  const maxBaseLength = MAX_STORAGE_NAME_LENGTH - suffix.length - extension.length
  return `${baseName.slice(0, maxBaseLength)}${suffix}${extension}`
}

export const uploadDocumentsPresigned = async (
  organizationIdentityId: IdentityId,
  files: File[],
  accessType: SourceAccessType,
  isReferenced: boolean,
  folderId: string | null,
  onProgress?: (progress: number) => void,
): Promise<IUploadDocumentsResponse> => {
  const emptyFile = files.find((file) => file.size === 0)
  if (emptyFile) throw new Error(`${emptyFile.name} is empty`)
  const longNameFile = files.find((file) => file.name.length > MAX_STORAGE_NAME_LENGTH)
  if (longNameFile) {
    throw new Error(`File name exceeds ${MAX_STORAGE_NAME_LENGTH} characters: ${longNameFile.name}`)
  }

  const totalBytes = files.reduce((total, file) => total + file.size, 0)
  const loadedByFile = new Array<number>(files.length).fill(0)
  const reportProgress = (): void => {
    if (!onProgress || totalBytes === 0) return
    const loadedBytes = loadedByFile.reduce((total, bytes) => total + bytes, 0)
    onProgress(Math.round((loadedBytes * 100) / totalBytes))
  }

  const uploadSlots = createUploadSlots()
  const uploaded = await Promise.all(
    files.map(async (file, index) => {
      await uploadSlots.acquire()
      try {
        const asset = await uploadFilePresigned(
          organizationIdentityId,
          new File([file], uniqueStorageName(file.name), { type: file.type }),
          AssetTypeEnum.enum.AIDocument,
          {
            isSecure: false,
            onProgress: (fraction) => {
              loadedByFile[index] = fraction * file.size
              reportProgress()
            },
          },
        )
        return { key: asset.key, file_name: file.name, file_size: file.size }
      } catch (error) {
        if (error instanceof MultipartUploadError) {
          void abortMultipartUpload(error.state).catch(() => undefined)
        }
        throw error
      } finally {
        uploadSlots.release()
      }
    }),
  )

  const response = await api.post<IUploadDocumentsResponse>('/ai/ingest-uploaded-documents/', {
    organization_identity_id: organizationIdentityId,
    documents: uploaded,
    access_type: accessType,
    is_referenced: isReferenced,
    folder_id: folderId,
  })
  return response.data
}
